import logger from '../../helpers/logger';
import CheckReaderDevice, { CheckCallback } from './CheckReaderDevice';
import { DeviceCmdResultData, kioDeviceListeners } from '../devices';
import { DeviceStatusFlags, KioDeviceStatus, RawKioDeviceStatus } from '../MonitoredDevice';

interface CheckData {
	ImagePath: string;
	MICR: string;
	Route: string;
	Acc: string;
	Orientation: string;
}

enum CheckOrientation {
	UNDETERMINED,
	FACE_DOWN_MICR_RIGHT,
	FACE_DOWN_MICR_LEFT,
	FACE_UP_MICR_RIGHT,
	FACE_UP_MICR_LEFT,
}

const STATUS_THROTTLE_MS = 500;

export default class PertechCheckReader implements CheckReaderDevice {
	private deviceName?: string;
	private onCheckRead?: CheckCallback;
	private isReading = false;
	private lastStatusCheck = 0;
	private lastStatus?: KioDeviceStatus;

	constructor(deviceName?: string) {
		this.deviceName = deviceName;
		kioDeviceListeners.push(this.onKioDeviceCmdResult.bind(this));
		this.cancelRead();
	}

	// Note: KioCheckReaderDevice.GetStatusEx() is slow and blocking, so it must be throttled to avoid UI hanging.
	//
	// Details object:
	// ub_stat - primary status, also reflected in KioDeviceStatus bits
	// ub_sstat - secondary status
	// ub_sens - bits indicating sensors:
	//      (0 is open, 1 is currently covered)
	//      Bit 0 = front right entry
	//      Bit 1 = front right inside
	//      Bit 2 = front left entry
	//      Bit 3 = front left inside
	//      Bit 4 = cover open
	//      Bit 5 = middle sensor
	getStatus(): KioDeviceStatus {
		try {
			const now = new Date().getTime();
			if (this.lastStatus && now - this.lastStatusCheck < STATUS_THROTTLE_MS) return this.lastStatus;

			this.lastStatusCheck = now;
			const status: RawKioDeviceStatus = JSON.parse(window.KioCheckReaderDevice.GetStatusEx(this.deviceName));
			this.lastStatus = { statusBits: status.Status, details: status.Details };

			return this.lastStatus;
		} catch {
			return { statusBits: DeviceStatusFlags.ReadStatusQueryFailed, details: {} };
		}
	}

	checkInserted(): boolean {
		const status = this.getStatus();
		return (status.details['ub_sens'] ?? '0') !== '0';
	}

	readCheck(stampVoid: boolean, onCheckRead: CheckCallback): void {
		if (this.isReading) return;
		this.isReading = true;
		this.onCheckRead = onCheckRead;
		window.KioCheckReaderDevice.SetPertechSetts(true, true, 1, stampVoid, this.deviceName);
	}

	onKioDeviceCmdResult(deviceName: string, _id: number, _cmd: number, err: number, data: DeviceCmdResultData) {
		if (this.deviceName && this.deviceName !== deviceName) return;
		if (!this.isReading) return;

		this.cancelRead();

		if (err !== DeviceStatusFlags.Ok) {
			logger.logCheckScanner.error({}, `Failed to read check with error code ${err}`, err.toString());
			this.onCheckRead?.(err, '', '', '', '');
			return;
		}

		const checkData = data as CheckData;
		let orientation = CheckOrientation.UNDETERMINED;
		if (checkData.Orientation) {
			const parsedOrientation = parseInt(checkData.Orientation);
			if (!isNaN(parsedOrientation)) orientation = parsedOrientation;
		}

		if (
			!checkData.ImagePath ||
			!checkData.MICR ||
			checkData.MICR === 'Unknown' ||
			!checkData.Acc ||
			!checkData.Route ||
			orientation !== CheckOrientation.FACE_DOWN_MICR_RIGHT
		) {
			logger.logCheckScanner.error({}, `Failed to read check data`, 'Failed to read check data');
			this.onCheckRead?.(DeviceStatusFlags.RecoverError, '', '', '', '');
			return;
		}

		this.onCheckRead?.(err, checkData.ImagePath, checkData.MICR, checkData.Acc, checkData.Route);
	}

	cancelRead(): void {
		if (!this.isReading) return;
		window.KioCheckReaderDevice.SetPertechSetts(false, true, 1, false, this.deviceName);
		this.isReading = false;
	}
}

const FAKE_READ_TIMEOUT = 15000;
const FAKE_SENSOR_TIMEOUT = 20000;

export class PertechCheckReaderStub implements CheckReaderDevice {
	private readTimeout = 0;
	private sensorTimeout = 0;
	private sensor = 0;

	getStatus(): KioDeviceStatus {
		return { statusBits: DeviceStatusFlags.Ok, details: { ub_stat: '0', ub_sstat: '0', ub_sens: `${this.sensor}` } };
	}

	checkInserted(): boolean {
		return !!this.sensor;
	}

	readCheck(_stampVoid: boolean, onCheckRead: CheckCallback): void {
		if (this.readTimeout !== 0) return;
		this.readTimeout = setTimeout(() => {
			this.readTimeout = 0;
			this.sensor = 1;
			this.simulateSensor();
			onCheckRead(DeviceStatusFlags.Ok, '', '', '1111111111', '999999992');
		}, FAKE_READ_TIMEOUT);
	}

	cancelRead(): void {
		if (this.readTimeout === 0) return;
		clearTimeout(this.readTimeout);
		this.readTimeout = 0;
	}

	private simulateSensor() {
		if (this.sensorTimeout !== 0) return;
		this.sensorTimeout = setTimeout(() => {
			this.sensorTimeout = 0;
			this.sensor = 0;
		}, FAKE_SENSOR_TIMEOUT);
	}
}
